import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon, type IconName } from "@/components/ui/Icon";
import { LegalLinks } from "@/components/ui/LegalLinks";
import { useAuth } from "@/hooks/useAuth";
import { useConfirm } from "@/hooks/useConfirm";
import { useToast } from "@/hooks/useToast";
import { useMyProfile } from "@/hooks/useMyProfile";
import { Avatar } from "@/components/account/Avatar";

/**
 * AccountMenu — 헤더 오른쪽 계정 버튼 + 드롭다운 (B′).
 *
 * 옛 헤더의 설정·도움말 아이콘을 여기로 모았다(웹메일은 2026-10-19 헤더 아이콘으로 복구 — 중복이라 여기선 뺌). 아바타 클릭 = 즉시 로그아웃 확인이던 동작은
 * 메뉴 맨 아래 "로그아웃" 으로 옮김(확인창은 그대로 useConfirm).
 * 12_통합관리_계정기기설계 §8-A(A-1/A-2): 머리 = 아바타(프로필 사진)·이름·소속·직위,
 * "설정" → "내 계정·환경"(onOpenSettings = Layout 이 내 계정 모달을 엶), "그룹 관리" → "통합관리"(admin 만, /group — 라우트는 C 묶음).
 * 드롭다운 모양은 알림 메뉴(.nf-menu)와 같은 틀(.acct-menu 가 폭·항목만 정의).
 */
interface AccountMenuProps {
  /** 내 계정·환경 열기 */
  onOpenSettings: () => void;
}

interface Row {
  key: string;
  icon: IconName;
  label: string;
  sub?: string;
  onClick: () => void;
}

export function AccountMenu({ onOpenSettings }: AccountMenuProps) {
  const { user, logout } = useAuth();
  const { photo, affiliation } = useMyProfile();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function handleLogout() {
    setOpen(false);
    if (!(await confirm({ title: "로그아웃할까요?", summary: "다시 사용하려면 로그인해야 합니다.", confirmLabel: "로그아웃" }))) return;
    await logout();
    navigate("/login", { replace: true });
  }

  const rows: Row[] = [
    { key: "settings", icon: "users", label: "내 계정·환경", onClick: () => { setOpen(false); onOpenSettings(); } },
    {
      key: "help", icon: "help", label: "도움말",
      onClick: () => { setOpen(false); toast.info("도움말은 준비 중입니다."); },
    },
  ];
  // 통합관리 — 전체 admin 만(법인 관리자에게는 보이지 않음). 법인 공간 밖 독립 페이지 /group(§0).
  const groupRow: Row | null = user?.isAdmin
    ? { key: "group", icon: "shield", label: "통합관리", sub: "관리자", onClick: () => { setOpen(false); navigate("/group"); } }
    : null;

  return (
    <div className="hd-notif-wrap acct-wrap" ref={ref}>
      <button
        type="button"
        className={`hd-account${open ? " is-open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={user ? `${user.name} 계정 메뉴` : "계정 메뉴"}
      >
        <Avatar className="hd-account-av" photo={photo} name={user?.name} loginId={user?.loginId} />
        <span className="hd-account-name">{user?.name || "사용자"}</span>
        <Icon name="chevD" size={14} />
      </button>
      {open && (
        <div className="nf-menu acct-menu" role="menu">
          <div className="qa-arrow" />
          <div className="acct-head ma-acct-head">
            <Avatar className="ma-av is-md" photo={photo} name={user?.name} loginId={user?.loginId} />
            <span className="ma-acct-head-text">
              <span className="acct-head-name">{user?.name || "사용자"}</span>
              <span className="acct-head-sub">{affiliation || user?.loginId}</span>
            </span>
          </div>
          {rows.map((r) => (
            <button key={r.key} type="button" role="menuitem" className="acct-item" onClick={r.onClick}>
              <Icon name={r.icon} size={16} />
              <span className="acct-item-label">{r.label}</span>
              {r.sub && <span className="acct-item-sub">{r.sub}</span>}
            </button>
          ))}
          {groupRow && (
            <>
              <div className="acct-sep" role="separator" />
              <button type="button" role="menuitem" className="acct-item" onClick={groupRow.onClick}>
                <Icon name={groupRow.icon} size={16} />
                <span className="acct-item-label">{groupRow.label}</span>
                <span className="badge ma-badge-admin">{groupRow.sub}</span>
              </button>
            </>
          )}
          <div className="acct-sep" role="separator" />
          <button type="button" role="menuitem" className="acct-item is-danger" onClick={() => void handleLogout()}>
            <Icon name="back" size={16} />
            <span className="acct-item-label">로그아웃</span>
          </button>
          <LegalLinks className="acct-legal" />
        </div>
      )}
    </div>
  );
}
